import React from 'react'
import {
  Box,
  Card,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles
} from '@material-ui/core'
import { FormattedDateTime } from '../../../formatted-date-time'
import { Link } from '../../../link'
import { TruncatedId } from '../../../truncated-id'
import { sortParentBoardRows } from './matching'
import type { ParentFanoutRecord } from './types'

const RECENT_ACK_WINDOW_MS = 5 * 60_000

const formatAcknowledged = (ackTimestamp: number, now: number) => {
  const ageMs = now - ackTimestamp
  if (ageMs >= 0 && ageMs < RECENT_ACK_WINDOW_MS) {
    const totalSeconds = Math.max(1, Math.floor(ageMs / 1000))
    if (totalSeconds < 60) {
      return `${totalSeconds} second${totalSeconds === 1 ? '' : 's'} ago`
    }
    const minutesAgo = Math.floor(totalSeconds / 60)
    return `${minutesAgo} minute${minutesAgo === 1 ? '' : 's'} ago`
  }
  return 'true'
}

const getAckDelayMs = (ackTimestamp: number, parentCreated: number) =>
  Math.max(0, Math.round(ackTimestamp - parentCreated))

const lerp = (start: number, end: number, t: number) => start + (end - start) * t

const ACK_DELAY_BASE_STOPS = [
  { seconds: 0, rgb: [110, 198, 128] as const }, // great - strong green
  { seconds: 5, rgb: [200, 226, 148] as const }, // great - warming toward ok
  { seconds: 10, rgb: [210, 232, 178] as const }, // ok
  { seconds: 20, rgb: [255, 210, 170] as const }, // getting worse
  { seconds: 30, rgb: [255, 175, 145] as const }, // orange-red
  { seconds: 60, rgb: [255, 190, 190] as const } // bad
]

const lighten = (rgb: readonly [number, number, number], amount = 0.45): [number, number, number] =>
  rgb.map(channel => Math.round(lerp(channel, 255, amount))) as [number, number, number]

const ACK_DELAY_COLOR_STOPS = ACK_DELAY_BASE_STOPS.map(stop => ({
  seconds: stop.seconds,
  rgb: lighten(stop.rgb)
}))

const getAckDelayCellStyle = (delayMs: number): React.CSSProperties => {
  const delaySeconds = Math.max(0, delayMs / 1000)
  const cappedSeconds = Math.min(delaySeconds, ACK_DELAY_COLOR_STOPS[ACK_DELAY_COLOR_STOPS.length - 1].seconds)

  let stopIndex = 0
  while (
    stopIndex < ACK_DELAY_COLOR_STOPS.length - 1 &&
    cappedSeconds > ACK_DELAY_COLOR_STOPS[stopIndex + 1].seconds
  ) {
    stopIndex += 1
  }

  const startStop = ACK_DELAY_COLOR_STOPS[stopIndex]
  const endStop = ACK_DELAY_COLOR_STOPS[Math.min(stopIndex + 1, ACK_DELAY_COLOR_STOPS.length - 1)]
  const segmentSpan = endStop.seconds - startStop.seconds
  const t = segmentSpan === 0 ? 0 : (cappedSeconds - startStop.seconds) / segmentSpan

  const rgb = startStop.rgb.map((channel, index) =>
    Math.round(lerp(channel, endStop.rgb[index], t))
  )

  return { backgroundColor: `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})` }
}

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    transition: theme.transitions.create(['box-shadow', 'border-color']),
    '&:hover': {
      boxShadow: theme.shadows[2]
    }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1)
  },
  headerMain: {
    minWidth: 0,
    flex: 1
  },
  headerTitleRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    minWidth: 0
  },
  headerTitle: {
    fontWeight: 600,
    flexShrink: 0
  },
  headerSeparator: {
    flexShrink: 0,
    lineHeight: 1
  },
  headerId: {
    minWidth: 0,
    '& .MuiTypography-root': {
      fontSize: theme.typography.pxToRem(11),
      lineHeight: 1.3,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
    }
  },
  headerMeta: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    flexShrink: 0,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.3,
    color: theme.palette.text.secondary
  },
  previewRow: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1),
    minWidth: 0
  },
  previewLabel: {
    flexShrink: 0,
    fontSize: theme.typography.pxToRem(11),
    lineHeight: 1.4
  },
  previewText: {
    flex: 1,
    minWidth: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.4,
    color: theme.palette.text.primary
  },
  statsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.5)
  },
  statPill: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0.75),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`
  },
  statPillPositive: {
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(76, 175, 80, 0.12)' : '#edf7ef',
    borderColor: theme.palette.type === 'dark' ? 'rgba(76, 175, 80, 0.35)' : '#c8e6c9'
  },
  statPillWarning: {
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 152, 0, 0.12)' : '#fff8e1',
    borderColor: theme.palette.type === 'dark' ? 'rgba(255, 152, 0, 0.35)' : '#ffe082'
  },
  statPillLabel: {
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.secondary,
    lineHeight: 1.3
  },
  statPillValue: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
    lineHeight: 1.3
  },
  tableSectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600
  },
  compactHeadCell: {
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.grey[200]}`
  },
  compactBodyCell: {
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.3,
    verticalAlign: 'top',
    borderBottom: `1px solid ${theme.palette.grey[100]}`
  },
  compactBodyText: {
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.3
  },
  compactTruncatedId: {
    '& .MuiTypography-root': {
      fontSize: theme.typography.pxToRem(12),
      lineHeight: 1.3
    }
  },
  boardTable: {
    width: '100%',
    tableLayout: 'fixed',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden'
  },
  boardCol: {
    width: '22%'
  },
  appearanceCol: {
    width: '38%'
  },
  ackCol: {
    width: '20%'
  },
  delayCol: {
    width: '20%'
  }
}))

const StatPill: React.FC<{ label: string; value: string | number; tone?: 'default' | 'positive' | 'warning' }> = ({
  label,
  value,
  tone = 'default'
}) => {
  const classes = useStyles()
  const toneClass =
    tone === 'positive' ? classes.statPillPositive : tone === 'warning' ? classes.statPillWarning : undefined

  return (
    <Box className={`${classes.statPill} ${toneClass || ''}`}>
      <Typography component='span' className={classes.statPillLabel}>
        {label}
      </Typography>
      <Typography component='span' className={classes.statPillValue}>
        {value}
      </Typography>
    </Box>
  )
}

export const ParentFanoutCard: React.FC<{ record: ParentFanoutRecord }> = ({ record }) => {
  const classes = useStyles()
  const [now, setNow] = React.useState(() => Date.now())
  const ackFetchFailed = record.ackStatus === 'error'

  React.useEffect(() => {
    const hasRecentAck = (currentNow: number) =>
      record.rows.some(row => {
        const appearanceId = row.matchedAppearance?.id
        if (!appearanceId) return false
        const ackTimestamp = record.acksByAppearanceId.get(appearanceId)
        return ackTimestamp != null && currentNow - ackTimestamp < RECENT_ACK_WINDOW_MS
      })

    if (!hasRecentAck(Date.now())) return

    const intervalId = window.setInterval(() => {
      const currentNow = Date.now()
      setNow(currentNow)
      if (!hasRecentAck(currentNow)) {
        window.clearInterval(intervalId)
      }
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [record.acksByAppearanceId, record.rows])
  const sortedRows = sortParentBoardRows(record.rows, record.parent.created, record.acksByAppearanceId, ackFetchFailed)
  const appearedCount = record.rows.filter(row => row.matchedAppearance).length
  const unknownCount = record.rows.filter(row => !row.matchedAppearance && row.coverageStatus === 'unknown').length
  const ackedCount = ackFetchFailed
    ? null
    : record.rows.filter(row => row.matchedAppearance && record.acksByAppearanceId.has(row.matchedAppearance.id)).length

  const allAcked = ackedCount != null && ackedCount === appearedCount && appearedCount > 0
  const hasUnknown = unknownCount > 0

  return (
    <Card className={classes.card} variant='outlined'>
      <Box className={classes.header}>
        <Box className={classes.headerMain}>
          <Box className={classes.headerTitleRow}>
            <Typography variant='subtitle1' className={classes.headerTitle}>
              Parent Appearance
            </Typography>
            <Typography variant='caption' color='textSecondary' className={classes.headerSeparator}>
              ·
            </Typography>
            <Box className={classes.headerId}>
              <TruncatedId value={record.parent.id} full path={`/message-appearance/${record.parent.id}`} />
            </Box>
          </Box>
        </Box>
        <Box className={classes.headerMeta}>
          <Typography variant='caption' color='textSecondary' className={classes.headerSeparator}>
            Created
          </Typography>
          <Typography variant='caption' color='textSecondary' className={classes.headerSeparator}>
            ·
          </Typography>
          <Typography variant='caption' color='textPrimary'>
            <FormattedDateTime value={record.parent.created} />
          </Typography>
        </Box>
      </Box>

      <Box className={classes.previewRow}>
        <Typography variant='caption' color='textSecondary' className={classes.previewLabel}>
          Message preview
        </Typography>
        <Typography variant='caption' color='textSecondary' className={classes.headerSeparator}>
          ·
        </Typography>
        <Typography component='span' className={classes.previewText}>
          {record.parent.message?.text || '(No text available)'}
        </Typography>
      </Box>
      <Box className={classes.statsRow}>
        <StatPill label='Acked' value={ackedCount == null ? '—' : ackedCount} tone={allAcked ? 'positive' : 'default'} />
        <StatPill label='Appeared' value={appearedCount} />
        <StatPill label='Unknown' value={unknownCount} tone={hasUnknown ? 'warning' : 'default'} />
        <StatPill label='Total' value={record.rows.length} />
      </Box>

      <Divider />

      <Box mt={1.5}>
        <Typography variant='subtitle2' className={classes.tableSectionTitle}>
          Child board fanout
        </Typography>
        <Table size='small' className={classes.boardTable}>
        <TableHead>
          <TableRow>
            <TableCell className={`${classes.compactHeadCell} ${classes.boardCol}`}>Board</TableCell>
            <TableCell className={`${classes.compactHeadCell} ${classes.appearanceCol}`}>Child Appearance</TableCell>
            <TableCell className={`${classes.compactHeadCell} ${classes.ackCol}`}>Acknowledged</TableCell>
            <TableCell className={`${classes.compactHeadCell} ${classes.delayCol}`}>Ack Delay</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map(row => {
            const matched = row.matchedAppearance
            const ackTimestamp = matched ? record.acksByAppearanceId.get(matched.id) : undefined
            const ackDelayMs = ackTimestamp != null ? getAckDelayMs(ackTimestamp, record.parent.created) : null
            const isUnknownRow = !matched && row.coverageStatus === 'unknown'
            return (
              <TableRow key={`${record.parent.id}-${row.board.id}`}>
                <TableCell className={classes.compactBodyCell}>
                  <Typography variant='caption' className={classes.compactBodyText}>
                    <Link to={`/boards/${row.board.id}`}>{row.board.title || row.board.id}</Link>
                  </Typography>
                </TableCell>
                <TableCell className={classes.compactBodyCell}>
                  {matched ? (
                    <Box className={classes.compactTruncatedId}>
                      <TruncatedId value={matched.id} full path={`/message-appearance/${matched.id}`} />
                    </Box>
                  ) : isUnknownRow ? (
                    <Typography variant='caption' color='textSecondary' className={classes.compactBodyText}>
                      Loading history...
                    </Typography>
                  ) : row.noAppearanceReason === 'no_content_changed' ? (
                    <Typography variant='caption' color='textSecondary' className={classes.compactBodyText}>
                      No appearance (content unchanged)
                    </Typography>
                  ) : (
                    <Typography variant='caption' color='textSecondary' className={classes.compactBodyText}>
                      No appearance
                    </Typography>
                  )}
                </TableCell>
                <TableCell className={classes.compactBodyCell}>
                  {!matched ? (
                    <Typography variant='caption' color='textSecondary' className={classes.compactBodyText}>
                      {isUnknownRow ? 'Loading...' : '—'}
                    </Typography>
                  ) : record.ackStatus === 'error' ? (
                    <Typography variant='caption' color='textSecondary' className={classes.compactBodyText}>
                      Unknown
                    </Typography>
                  ) : record.ackStatus === 'loading' ? (
                    '...'
                  ) : ackTimestamp != null ? (
                    formatAcknowledged(ackTimestamp, now)
                  ) : (
                    'false'
                  )}
                </TableCell>
                <TableCell
                  className={classes.compactBodyCell}
                  style={
                    ackDelayMs != null && record.ackStatus === 'ready'
                      ? getAckDelayCellStyle(ackDelayMs)
                      : undefined
                  }
                >
                  {!matched ? (
                    <Typography variant='caption' color='textSecondary' className={classes.compactBodyText}>
                      {isUnknownRow ? 'Loading...' : '—'}
                    </Typography>
                  ) : record.ackStatus === 'error' ? (
                    <Typography variant='caption' color='textSecondary' className={classes.compactBodyText}>
                      Unknown
                    </Typography>
                  ) : record.ackStatus === 'loading' ? (
                    '...'
                  ) : ackDelayMs != null ? (
                    `${ackDelayMs} ms`
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      </Box>
    </Card>
  )
}
